import { yupResolver } from '@hookform/resolvers/yup';
import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';

import { PermissionsEnum } from '@/auth/interfaces';
import { TIPO_USO_GRUPO_IPV4 } from '@/erp/sytem-sttings/ipv4-groups/shared/constants';
import { gridSizeMdLg12, gridSizeMdLg6, ROUTER_PATHS, useCheckPermissions, useLoaders } from '@/shared';
import {
  CreateIpv6GroupParams,
  useCreateIpv6Group,
  useUpdateIpv6Group,
} from '@/shared/actions/erp';
import {
  CustomAutocompleteArrString,
  CustomNumberTextField,
  CustomTextField,
  CustomTextFieldNoForm,
  LoaderComponent,
  SampleCheckbox,
  SingleFormBoxScene,
  TabTextLabel,
} from '@/shared/components/ui';
import { Router } from '@/shared/interfaces';
import { Ipv6Group } from '@/shared/interfaces/erp/system-settings';
import { newIpv6Schema } from '@/shared/utils';
import { useFetchRouters } from '@/store/erp';
import { Grid, Typography } from '@mui/material';
import CustomAutocompleteMultiple from '@/erp/sytem-sttings/promociones/shared/components/AcutocompletePromocion';
import { CustomAlertCard } from '@/shared/components/ui/Alerts';

export type SaveIpv6GroupProps = {
  titlePage: string;
  editIpv6Group?: Ipv6Group;
};

type SaveFormData = CreateIpv6GroupParams & {};

const SaveIpv6Group: React.FC<SaveIpv6GroupProps> = ({
  titlePage,
  editIpv6Group,
}) => {
  useCheckPermissions(PermissionsEnum.api_view_router);
  const navigate = useNavigate();
  const returnUrlIPv6Page = ROUTER_PATHS.configuracionRED.grupoIpv6Nav;

  const [shrink, setShrink] = useState(false);

  ///* mutations
  const createIpv4GroupMutation = useCreateIpv6Group({
    navigate,
    returnUrl: returnUrlIPv6Page,
  });
  const updateIpv4GroupMutation = useUpdateIpv6Group({
    navigate,
    returnUrl: returnUrlIPv6Page,
  });

  ///* form
  const form = useForm<SaveFormData>({
    resolver: yupResolver(newIpv6Schema) as any,
  });
  const {
    handleSubmit,
    reset,
    formState: { errors, isValid },
  } = form;

  ///* fetch data
  const {
    data: routersPagingRes,
    isLoading: isLoadRouters,
    isRefetching: isRefetchingRouters,
  } = useFetchRouters({
    tiene_grupo_ipv_6: false,
    page_size: 1000,
  });

  ///* handlers
  const onSave = () => {
    if (!isValid) return;

    ///* edit
    if (editIpv6Group?.id_grupoipv6) {
      return updateIpv4GroupMutation.mutate({
        id: editIpv6Group.id_grupoipv6,
        data: { ...form.getValues() },
      });
    }

    ///* create
    return createIpv4GroupMutation.mutate({
      ...form.getValues(),
      estado: !!+form.getValues().estado,
    });
  };

  ///* effects
  useEffect(() => {
    if (!editIpv6Group?.id_grupoipv6) return;
    setShrink(true);
    reset({
      ...editIpv6Group,
    } as any);
  }, [editIpv6Group]);

  useLoaders(isLoadRouters || isRefetchingRouters);
  if (!!editIpv6Group && (isLoadRouters || isRefetchingRouters))
    return <LoaderComponent />;

  return (
    <SingleFormBoxScene
      titlePage={titlePage}
      onCancel={() => {
        navigate(returnUrlIPv6Page);
      }}
      onSave={handleSubmit(onSave, (e) => {
        console.log(e);

      })}
    >

      {+form.watch().cidr < 16 && (
        <Grid item xs={12}>
          <CustomAlertCard
            alertTitle='Información'
            alertMessage='Estás a punto de crear una barra inferior a la recomendada. Tomar en cuenta que el máximo de IPs creadas es de 65,536.'
          />
        </Grid>
      )

      }
      <CustomTextField
        label="Nombre"
        name="nombre"
        control={form.control}
        defaultValue={form.getValues().nombre || ''}
        error={errors.nombre}
        helperText={errors.nombre?.message}
      />

      {/* <CustomAutocomplete<Router>
        label="Router"
        name="router"
        textFieldKey="nombre"
        defaultValue={form.getValues().router || ''}
        // options
        valueKey="nombre"
        actualValueKey="codigo"
        options={routersPagingRes?.data.data || []}
        isLoadingData={isLoadRouters}
        disableClearable
        // errors
        control={form.control}
        error={errors.router as any}
        helperText={errors.router?.message}
        size={gridSizeMdLg6}
      /> */}

      <CustomTextField
        label="Pool IP"
        name="ipv_6"
        control={form.control}
        defaultValue={form.getValues().ipv_6 || ''}
        error={errors.ipv_6}
        helperText={errors.ipv_6?.message}
        shrink={shrink}
        size={gridSizeMdLg6}
      />
      <CustomNumberTextField
        label="CIDR"
        name="cidr"
        defaultValue={form.getValues().cidr || 32}
        control={form.control}
        error={errors.cidr}
        helperText={errors.cidr?.message}
        size={gridSizeMdLg6}
      />

      <CustomAutocompleteArrString
        label="Tipo de uso"
        name="tipo_uso"
        options={TIPO_USO_GRUPO_IPV4}
        control={form.control}
        disableClearable
        defaultValue={form.getValues().tipo_uso || ''}
        error={errors.tipo_uso}
        helperText={errors.tipo_uso?.message?.toString() ?? ''}
        isLoadingData={false}
        size={gridSizeMdLg6}
      />

      <SampleCheckbox
        label="Estado"
        name="estado"
        control={form.control}
        defaultValue={!!form.getValues().estado}
        isState
        size={gridSizeMdLg6}
      />

      {!!editIpv6Group && (
        <>
          <Grid item xs={12}>
            <TabTextLabel text={'Router Asiganado'} mt={0} pb={2} pl={0} pt={0} />
          </Grid>

          {
            editIpv6Group?.router_data?.map((value, index) => {
              return <CustomTextFieldNoForm
                key={index}
                disabled={true}
                label={`Router Asignado ${index + 1}`}
                defaultValue={value.nombre}
                size={gridSizeMdLg6}
              />
            })

          }
        </>
      )
      }

      <Grid item xs={12}>
        <Typography variant='body2'>
          Nota: Una vez que se asigna un router, no se puede cambiar ni eliminar.
        </Typography>
      </Grid>

      <CustomAutocompleteMultiple<Router>
        label="Router"
        name="routers"
        textFieldKey="custom-autocomplete-router"
        valueKey="nombre"
        actualValueKey="codigo"
        // options
        options={routersPagingRes?.data.data || []}
        defaultValue={
          []
        }
        isLoadingData={isLoadRouters}
        // errors
        control={form.control}
        error={!!errors.routers}
        helperText={errors.routers?.message}
        disabled={false}
        size={gridSizeMdLg12}
        useSpecificKey
        specificKey="codigo"
      />

    </SingleFormBoxScene>
  );
};

export default SaveIpv6Group;
